import {UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms'
import {Observable} from 'rxjs'
import {
  Answer,
  AnswerFormGroup,
  AnswerModelType,
  MultiAnswerControls,
  Question,
  SelectOption,
  SelectOptions,
} from './dynamic-form'
import {map, tap} from 'rxjs/operators'
import {atLeastOneSelected} from '../../../core/forms/at-least-one-selected.validator'

export class MultiAnswerFormGroup extends UntypedFormGroup implements AnswerFormGroup {
  readonly type = AnswerModelType.MULTI_ANSWER
  readonly question: Question
  declare controls: MultiAnswerControls
  readonly additionalInformationRequired$: Observable<boolean>

  constructor(question: Question, defaultAnswer?: Answer) {
    super({
      answers: new UntypedFormArray(
        (question.options as SelectOptions).map(o => new UntypedFormControl(defaultAnswer?.answerIds?.includes(o.key))),
        question.required ? atLeastOneSelected() : [],
      ),
      additionalInformation: new UntypedFormControl(
        {value: defaultAnswer?.additionalInformation, disabled: true},
        Validators.required,
      ),
    })
    this.question = question

    this.additionalInformationRequired$ = this.controls.answers.valueChanges.pipe(
      map(() => this.isAdditionalInfoRequired(question, this.controls.answers)),
      tap(required =>
        required ? this.controls.additionalInformation.enable() : this.controls.additionalInformation.disable(),
      ),
    )
  }

  get answerControls(): UntypedFormControl[] {
    return this.controls.answers.controls as UntypedFormControl[]
  }

  toAnswer(): Answer {
    const result: Answer = {
      questionId: this.question.id,
      answerIds: this.getSelectedOptions(this).map(option => option?.key),
    }
    if (this.controls.additionalInformation.enabled) {
      result.additionalInformation = this.controls.additionalInformation.value
    }
    return result
  }

  private isAdditionalInfoRequired(question: Question, answers: UntypedFormArray): boolean {
    if (!Array.isArray(question.options)) {
      return false // only on selectable options we allow additionalInformation input
    }
    const optionsWithAdditionalInfoRequired = answers.controls
      .map((control, index) => {
        return {
          control,
          option: this.getSelectOptionByIndex(index),
        }
      })
      .filter(({control}) => control.value) // only those that are set to true
      .filter(({option}) => option?.requireAdditionalInformation) // only where required
    return optionsWithAdditionalInfoRequired && optionsWithAdditionalInfoRequired?.length > 0
  }

  private getSelectedOptions(model: MultiAnswerFormGroup): SelectOption[] {
    return model.controls.answers.controls
      .map((control: UntypedFormControl, index) => {
        return {
          control,
          option: this.getSelectOptionByIndex(index),
        }
      })
      .filter(({control}) => control.value) // only those that are set to true
      .map(({option}) => option)
  }

  private getSelectOptionByIndex(index: number): SelectOption {
    if (!Array.isArray(this.question.options)) {
      throw RangeError('could not find selectOption since options is not an array')
    }
    return this.question.options[index]
  }
}
